import { useCallback, useEffect, useState } from 'react'
import { RequestError } from 'api/errors'

type FetchResponse<T> = {
  result: T | undefined,
  error: RequestError | null,
}

/** Runs the request on mount; API errors end up in response.error, other errors are thrown */
export const useFetch = <T>(fetchFn: () => Promise<T>) => {
  const [isLoading, setLoading] = useState(false)
  const [response, setResponse] = useState<FetchResponse<T>>({ error: null, result: undefined })

  const refetch = useCallback(async () => {
    setLoading(true)
    try {
      setResponse({ result: await fetchFn(), error: null })
      setLoading(false)
    } catch (e) {
      if (e instanceof RequestError) {
        setResponse({ result: undefined, error: e })
        setLoading(false)
        return
      }
      throw e
    }
  }, [fetchFn])

  useEffect(() => {
    refetch()
    // Only on mount, like the JS version: callers pass inline functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { isLoading, refetch, response }
}
